import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useNFLLeague } from '@/contexts/NFLLeagueContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Gavel, Trophy, Users, Clock, Search, Play, CheckCircle2, AlertCircle, Bomb, ChevronDown, ChevronUp, X, Plus, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import HideDraftedControl from './draftroom/HideDraftedControl';
import { PlayerAvailabilityBadge, isUnavailable } from './PlayerAvailabilityBadge';
import { useToast } from '@/hooks/use-toast';
import { useCommissionerFeeStatus } from '@/hooks/useCommissionerFeeStatus';
import NFLPlayerDetailModal from './NFLPlayerDetailModal';
import PlayerNewsIcon from '@/components/player/PlayerNewsIcon';
import { ActiveNewsProvider } from '@/components/player/ActiveNewsContext';
import NFLDraftSabotagePanel from './NFLDraftSabotagePanel';
import { useDraftSabotageEffects, DraftSabotageEffect, SabotageCardLite } from '@/hooks/useDraftSabotageEffects';
// SAB-842: shared, presentational draft components (no DB access of their own). Reused
// rather than reimplemented for NFL; both are fed NFL-shaped props.
import DraftRoster from '@/components/draft/DraftRoster';
import DraftOrderStrip from '@/components/draft/DraftOrderStrip';
import DraftBoard from '@/components/draft/DraftBoard';
import DraftChatLive from '@/components/draft/DraftChatLive';
import { useNFLDraftQueue } from '@/hooks/useNFLDraftQueue';
import { useNFLAutoDraft } from '@/hooks/useNFLAutoDraft';
import { useNFLDraftState, useNFLDraftSync } from '@/hooks/useNFLDraftData';
import { NFL_DRAFT_CHAT } from '@/hooks/useDraftChat';
import { getDraftBoardPosColor, getBoardCellColor } from '@/lib/nflDraftBoardColors';
import { filterDraftPool, poolPositionFiltersFor, type PoolPositionFilter } from '@/lib/filterDraftPool';
import { loadHideDrafted, saveHideDrafted } from '@/lib/nflDraftRoomPrefs';
import { leagueSlotOrder } from '@/lib/nflRosterSlots';
import { useLeagueRosterability } from '@/hooks/useStartablePositions';
import { excludeUnrosterable } from '@/lib/nflStartablePositions';
import type { DraftPickLog } from '@/types/draftPlayer';

/**
 * SAB-886: the league's slot order now comes from `leagueSlotOrder`, shared with the V2
 * room, My Team, Matchup and Roster Settings.
 *
 * This file previously held its own expanded 21-slot constant. Its comment was right about
 * the stakes -- "anything holding a slot absent from here silently disappears from the
 * view" -- and that is precisely what happened: no copy of the list contained FLEX2, so in
 * a two-FLEX league the player the server assigned to FLEX2 had no row and vanished. The
 * list was also wrong the other way, rendering IDP1/IDP2 to leagues with no IDP slot.
 *
 * IR is still included and the reasoning is unchanged: prod carries 120 rows at IR1/IR2
 * from before SAB-803, and omitting IR would hide 120 real players from their own
 * managers. It is included by being in the league's own array, not by being hardcoded.
 */

interface DraftPick {
  id: string;
  round: number;
  pick_number: number;
  pick_in_round: number;
  team_id: string;
  player_id: string | null;
  player_name: string | null;
  player_position: string | null;
  player_nfl_team: string | null;
  // SAB-847: FK-joined from nfl_franchises so the draft board can show a bye without a
  // second query. All 32 franchises carry a non-null bye (5-14), verified on prod.
  player_bye_week: number | null;
  headshot_url: string | null;
  auto_picked: boolean;
  picked_by_commissioner: boolean;
}

interface AvailablePlayer {
  id: string;
  full_name: string;
  position: string;
  adp: number | null;
  // SAB-749: roster availability (ACT / IR / PUP / NON / EXE / UFA ...). Display only —
  // never used to reorder, filter or discount. See PlayerAvailabilityBadge.
  status: string | null;
  // SAB-457 (2): TRASH / ANTI projections + franchise FK for upcoming BYE join.
  // Not rendered yet — wired into the table in commit 4.
  proj_sab_pts: number | null;
  projected_anti_points: number | null;
  nfl_franchise_id: string | null;
  // SAB-457 (3): FK-joined bye_week_2026 from nfl_franchises (cached at
  // migration time from nfl_games; fail-loud derivation guarantees all
  // 32 franchises have a non-null value in 5-14).
  // SAB-457 (7): alias surfaces in the search filter (e.g. "BAL", "DAL").
  franchise: { bye_week_2026: number | null; alias: string | null } | null;
  headshot_url: string | null;
}

// SAB-457 (8): spec-exact position colors scoped to the draft board only.
// Uses spec HSLs from docs/draft-redesign/colors_and_type.css (literal
// strings so Tailwind's content scanner picks them up). Other surfaces
// keep the existing POS_COLORS below until their own redesign tickets.
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0 || parts[0] === '') return '?';
  if (parts.length === 1) return (parts[0][0] ?? '?').toUpperCase();
  return ((parts[0][0] ?? '') + (parts[parts.length - 1][0] ?? '')).toUpperCase();
}

const POS_COLORS: Record<string, string> = {
  QB: 'bg-red-500/15 text-red-400 border-red-500/30',
  RB: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  WR: 'bg-green-500/15 text-green-400 border-green-500/30',
  TE: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
  K: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  P: 'bg-teal-500/15 text-teal-400 border-teal-500/30',
  DST: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/30',
  COACH: 'bg-pink-500/15 text-pink-400 border-pink-500/30',
  DB: 'bg-indigo-500/15 text-indigo-400 border-indigo-500/30',
  LB: 'bg-orange-500/15 text-orange-400 border-orange-500/30',
  DL: 'bg-rose-500/15 text-rose-400 border-rose-500/30',
  IDP: 'bg-violet-500/15 text-violet-400 border-violet-500/30',
};

// SAB-955: the tab list is derived per-league by poolPositionFiltersFor, shared with the
// V2 room. It used to be a hardcoded duplicate of POOL_POSITION_FILTERS, which offered an
// IDP tab to leagues with no IDP slot -- a tab that returns nothing on every click.

// SAB-803. Announced on the pre-draft panel, where settings.draft_rounds does not exist
// yet — it is written by nfl-initialize-draft when the draft starts. So this cannot be read
// from the league and has to be a literal; naming it at least makes the duplication findable.
// MUST track `rounds || 19` in supabase/functions/nfl-initialize-draft/index.ts.
// NOT the roster cap: that is 21 (13 starters + 6 bench + 2 IR) and lives in NFLPlayersPage.
const DEFAULT_DRAFT_ROUNDS = 19;

export default function NFLDraftPage() {
  const { currentLeague, currentTeam, teams, isLoading: contextLoading } = useNFLLeague();

  // SAB-955: positions this league can start; drives both the tabs and the pool.
  // SAB-998: same hook as both players surfaces; one eligibility read serves all of it.
  const { startable, hasFlex, flex, idp } = useLeagueRosterability(currentLeague?.settings?.roster_slots);
  const positionFilters = useMemo(
    () => poolPositionFiltersFor(startable, { hasFlex, flex, idp }),
    [startable, hasFlex, flex, idp],
  );

  // SAB-886: THIS league's slots. Falls back to the default for the wiped leagues.
  const rosterSlotOrder = useMemo(
    () => leagueSlotOrder(currentLeague?.settings?.roster_slots),
    [currentLeague?.settings],
  );
  const { user } = useAuth();
  const [view, setView] = useState<'round' | 'team' | 'board' | 'recap'>('round');

  // Draft data (picks, league state, clock, roster slots, player pool). Extracted verbatim
  // to src/hooks/useNFLDraftData.ts so the redesigned room reads the same data through the
  // same code. Split in two to preserve the original hook ORDER -- see that file's header.
  const draftData = useNFLDraftState();
  const {
    picks, isLoading, draftStatus, pauseReason, pausedRemaining, draftPausedAt,
    currentPickNumber, totalPicks, pickDeadline, onTheClockTeamId, timeLeft,
    slotByPlayerId, availablePlayers, playersLoading,
  } = draftData;

  // Live draft state
  const [pauseBusy, setPauseBusy] = useState(false);
  const [undoBusy, setUndoBusy] = useState(false);
  // SAB-534 Chunk 4: commissioner auto-draft-all (client re-invokes the
  // server-chunked loop until the draft is complete; cancel ref stops it).
  const [autoDraftAllBusy, setAutoDraftAllBusy] = useState(false);
  const [autoDraftProgress, setAutoDraftProgress] = useState('');
  const autoDraftCancelRef = useRef(false);
  const [isPicking, setIsPicking] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [posFilter, setPosFilter] = useState('ALL');
  // SAB-932: hide drafted players while BROWSING. Never while searching -- that rule
  // lives in filterDraftPool, shared with the V2 room so the two cannot diverge.
  const [hideDrafted, setHideDrafted] = useState(true);
  useEffect(() => {
    if (currentLeague?.id) setHideDrafted(loadHideDrafted(currentLeague.id));
  }, [currentLeague?.id]);
  const toggleHideDrafted = useCallback(() => {
    setHideDrafted(prev => {
      const next = !prev;
      saveHideDrafted(currentLeague?.id, next);
      return next;
    });
  }, [currentLeague?.id]);
  const pickLogRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const [detailPlayerId, setDetailPlayerId] = useState<string | null>(null);
  // SAB-1172: the news badge opens the sheet straight on News.
  const [detailTab, setDetailTab] = useState<'stats' | 'news' | undefined>(undefined);

  // SAB-842: playerId -> authoritative roster slot, from nfl_rosters.

  // SAB-193 Phase 3: draft sabotage panel + effects
  const [sabotagePanelOpen, setSabotagePanelOpen] = useState(false);
  const [screenShake, setScreenShake] = useState(false);
  const shakeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const teamMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const t of teams) map[t.id] = t.name;
    return map;
  }, [teams]);

  const isMyTurn = useMemo(() => {
    if (!currentTeam || !onTheClockTeamId) return false;
    return onTheClockTeamId === currentTeam.id;
  }, [currentTeam, onTheClockTeamId]);

  const isCommissioner = useMemo(() => {
    if (!currentLeague || !user) return false;
    return currentLeague.commissioner_id === user.id;
  }, [currentLeague, user]);

  // SAB-591: paid leagues — the server refuses draft start (403
  // COMMISSIONER_UNPAID) until the commissioner's entry fee is in.
  const commissionerFee = useCommissionerFeeStatus(currentLeague?.id);
  const commissionerUnpaid = commissionerFee.required && !commissionerFee.loading && !commissionerFee.paid;

  // SAB-193: live draft sabotage effects + Realtime subscription
  const { activeEffects, refetchActive } = useDraftSabotageEffects({
    leagueId: currentLeague?.id ?? null,
    myTeamId: currentTeam?.id ?? null,
    draftStatus,
    onEvent: (effect: DraftSabotageEffect, card: SabotageCardLite | null) => {
      const attackerName = teams.find((t) => t.id === effect.attacker_team_id)?.name || 'Someone';
      const targetName = effect.target_team_id
        ? teams.find((t) => t.id === effect.target_team_id)?.name || 'a team'
        : 'the league';
      toast({
        title: `${card?.emoji || '💣'} ${card?.name || 'Sabotage'} played`,
        description: `${attackerName} → ${targetName}`,
      });
    },
    onSabotaged: () => {
      // Restart shake — clear any pending timeout so back-to-back hits
      // re-trigger the animation instead of leaving a stale flag.
      if (shakeTimeoutRef.current) clearTimeout(shakeTimeoutRef.current);
      setScreenShake(false);
      requestAnimationFrame(() => setScreenShake(true));
      shakeTimeoutRef.current = setTimeout(() => setScreenShake(false), 600);
    },
  });

  const { fetchDraftState, fetchPicks, fetchRosterSlots, fetchAvailablePlayers } =
    useNFLDraftSync({ league: currentLeague, state: draftData, refetchActive });

  useEffect(() => () => {
    if (shakeTimeoutRef.current) clearTimeout(shakeTimeoutRef.current);
  }, []);

  // Active-effect predicates for the current viewer.
  const myTeamId = currentTeam?.id;
  // SAB-450 (2): league_guess_who applies to EVERYONE including the attacker.
  // Prior code exempted the attacker (attacker_team_id !== myTeamId), which
  // meant the user who played Roughing the Drafter saw the banner but their
  // own board stayed unmasked — the reported "signature mechanic is a no-op"
  // case. Spec is unambiguous ("Everyone is in Guess Who mode this round"),
  // and game-design-wise the attacker keeping perfect info while others go
  // blind would be OP. hide_info stays per-target (single-team scope).
  const guessWhoActive = useMemo(
    () => activeEffects.some((e) =>
      (e.effect_type === 'hide_info' && e.target_team_id === myTeamId) ||
      (e.effect_type === 'league_guess_who')
    ),
    [activeEffects, myTeamId],
  );
  const fakeNewsEffect = useMemo(
    () => activeEffects.find((e) => e.effect_type === 'fake_injury' && e.target_team_id === myTeamId) || null,
    [activeEffects, myTeamId],
  );
  const delayOfGameOnMe = useMemo(
    () => activeEffects.some((e) => e.effect_type === 'timer_half' && e.target_team_id === myTeamId),
    [activeEffects, myTeamId],
  );
  const blockedPuntOnMe = useMemo(
    () => activeEffects.some((e) => e.effect_type === 'force_auto' && e.target_team_id === myTeamId),
    [activeEffects, myTeamId],
  );
  const snakeReversalActive = useMemo(
    () => activeEffects.find((e) => e.effect_type === 'reverse_order') || null,
    [activeEffects],
  );
  const roughingActive = useMemo(
    () => activeEffects.find((e) => e.effect_type === 'league_guess_who') || null,
    [activeEffects],
  );

  // SAB-450 (5) / SAB-33: pick-log mask boundary.
  // When guessWhoActive is true for the viewer, mask any pick whose
  // pick_number is at or after the EARLIEST currently-active mask play.
  // The event row records `pick_number = currentPickNumber` at play time
  // (the pick on the clock when the card was played), so the on-clock pick
  // and every subsequent pick within the active window get masked.
  // Picks made BEFORE the card was played stay fully visible — you can't
  // unsee what was already public, and the strategic point is hiding the
  // upcoming picks.
  // hide_info (per-team) only contributes when the current viewer is the
  // target — mirrors how guessWhoActive itself is per-viewer.
  const maskedSincePick = useMemo<number | null>(() => {
    if (!guessWhoActive) return null;
    const relevant = activeEffects.filter((e) =>
      e.effect_type === 'league_guess_who'
      || (e.effect_type === 'hide_info' && e.target_team_id === myTeamId)
    );
    if (relevant.length === 0) return null;
    const earliest = relevant.reduce<number>(
      (min, e) => Math.min(min, e.pick_number ?? Number.POSITIVE_INFINITY),
      Number.POSITIVE_INFINITY,
    );
    return Number.isFinite(earliest) ? earliest : null;
  }, [activeEffects, guessWhoActive, myTeamId]);
  const isPickMasked = useCallback(
    (pickNumber: number) =>
      maskedSincePick != null && pickNumber >= maskedSincePick,
    [maskedSincePick],
  );

  // SAB-450 (4): persistent effect-active rail — countdown per active
  // round-scoped or pick-scoped effect. Recomputes whenever currentPickNumber
  // ticks, so the rail counts down live as picks land.
  // Round-scoped (Roughing the Drafter, Snake Reversal, league Guess Who):
  //   expires_at_round * picksPerRound = last pick of the window.
  //   remaining = lastPick - currentPickNumber + 1.
  // Pick-scoped (single-target Guess Who, Delay of Game, Blocked Punt,
  //   Fake News): expires_at_pick is the pick at which the effect lifts.
  const picksPerRound = teams.length || 12;
  const describeEffectExpiry = useCallback(
    (effect: { expires_at_round: number | null; expires_at_pick: number | null }): string => {
      if (effect.expires_at_round != null) {
        const lastPick = effect.expires_at_round * picksPerRound;
        const remaining = Math.max(0, lastPick - currentPickNumber + 1);
        if (remaining === 0) return 'Ending now';
        if (remaining === 1) return 'Ends after this pick';
        return `Ends in ${remaining} picks · after Round ${effect.expires_at_round}`;
      }
      if (effect.expires_at_pick != null) {
        const remaining = Math.max(0, effect.expires_at_pick - currentPickNumber + 1);
        if (remaining === 0) return 'Ending now';
        if (remaining === 1) return 'Ends after this pick';
        return `Ends in ${remaining} picks`;
      }
      return '';
    },
    [currentPickNumber, picksPerRound],
  );
  // Soonest-ending mask among my currently-active Guess Who sources (the
  // sub-banner shows ONE countdown; pick the most-restrictive).
  const guessWhoExpiryText = useMemo(() => {
    if (!guessWhoActive) return '';
    const relevant = activeEffects.filter((e) =>
      e.effect_type === 'league_guess_who'
      || (e.effect_type === 'hide_info' && e.target_team_id === myTeamId),
    );
    if (relevant.length === 0) return '';
    let minRemaining = Number.POSITIVE_INFINITY;
    let chosen: typeof relevant[number] | null = null;
    for (const e of relevant) {
      const rem = e.expires_at_round != null
        ? e.expires_at_round * picksPerRound - currentPickNumber + 1
        : (e.expires_at_pick ?? Number.POSITIVE_INFINITY) - currentPickNumber + 1;
      if (rem < minRemaining) { minRemaining = rem; chosen = e; }
    }
    return chosen ? describeEffectExpiry(chosen) : '';
  }, [guessWhoActive, activeEffects, myTeamId, currentPickNumber, picksPerRound, describeEffectExpiry]);

  // Auto-scroll to latest pick
  useEffect(() => {
    if (pickLogRef.current) {
      pickLogRef.current.scrollTop = pickLogRef.current.scrollHeight;
    }
  }, [picks]);

  // Make a pick
  const handlePick = async (playerId: string) => {
    if (!currentLeague || isPicking) return;
    setIsPicking(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      if (!token) { toast({ title: 'Not logged in', variant: 'destructive' }); return; }

      const res = await fetch(
        `${(supabase as any).supabaseUrl}/functions/v1/nfl-make-pick`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            league_id: currentLeague.id,
            player_id: playerId,
            commissioner_pick: isCommissioner && !isMyTurn,
          }),
        }
      );
      const result = await res.json();
      if (result.error) {
        toast({ title: 'Pick failed', description: result.error, variant: 'destructive' });
      } else {
        toast({ title: 'Pick made!', description: `${result.pick.player_name} (${result.pick.position})` });
        fetchPicks();
        fetchDraftState();
        fetchAvailablePlayers();
      }
    } catch (err) {
      toast({ title: 'Error', description: String(err), variant: 'destructive' });
    } finally {
      setIsPicking(false);
    }
  };

  // Start draft (commissioner only)
  const handleStartDraft = async () => {
    if (!currentLeague) return;
    setIsPicking(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      if (!token) return;

      const res = await fetch(
        `${(supabase as any).supabaseUrl}/functions/v1/nfl-initialize-draft`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ league_id: currentLeague.id }),
        }
      );
      const result = await res.json();
      if (result.error) {
        // SAB-591: the client disables Start while unpaid, but if the server
        // gate fires anyway, say the same friendly thing — not the raw code.
        const description = result.code === 'COMMISSIONER_UNPAID'
          ? 'Pay your entry fee first — the league cannot start until the commissioner has paid.'
          : result.error;
        toast({ title: 'Failed to start draft', description, variant: 'destructive' });
      } else {
        toast({ title: 'Draft started!', description: `${result.total_picks} picks across ${result.rounds} rounds` });
        fetchDraftState();
        fetchPicks();
      }
    } catch (err) {
      toast({ title: 'Error', description: String(err), variant: 'destructive' });
    } finally {
      setIsPicking(false);
    }
  };

  // SAB-457 (4 + 5): filter + sort.
  //
  // sort === null  → default mode (TRASH-desc two-group): projected on top
  //                  sorted by proj_sab_pts desc, divider, unprojected below
  //                  sorted by ADP asc and dimmed. Catches the "85% of the
  //                  pool has null trash" reality so the unprojected players
  //                  stay reachable.
  // sort !== null  → user picked an explicit sort: single flat list, standard
  //                  nulls-last for the chosen key.
  //
  // Header click cycle (3 states, per ticket UX):
  //   inactive → set to column's natural default dir
  //   active + default dir → flip to non-default
  //   active + non-default → clear back to null (default two-group)
  // Default dirs: TRASH/ANTI desc (high = better in inverted scoring);
  //               ADP/BYE asc (low = earlier pick / earlier bye).
  const [sort, setSort] = useState<{ key: 'trash' | 'anti' | 'adp' | 'bye'; dir: 'asc' | 'desc' } | null>(null);
  // SAB-459: the queue is now PERSISTED, not per-session state. It survives a refresh and
  // nfl-auto-pick reads it as priority 1. Optimistic with rollback -- a queue that shows
  // players it did not actually save would tell a manager they are covered when they are
  // not, which is the one lie this control must never tell.
  const {
    queue, toggle: toggleQueue, reorder: reorderQueue, remove: removeFromQueue,
  } = useNFLDraftQueue({
    leagueId: currentLeague?.id ?? null,
    teamId: currentTeam?.id ?? null,
    onError: (message) => toast({
      title: 'Queue not saved', description: message, variant: 'destructive',
    }),
  });
  const queueSet = useMemo(() => new Set(queue), [queue]);

  // SAB-840 / SAB-853 autodraft. Re-read on every pick advance, because the server flips
  // this ON as part of making a pick -- a manager whose clock lapses must see it start
  // costing them picks without reloading the page.
  const {
    enabled: autoDraftOn, saving: autoDraftSaving, setAutoDraft,
  } = useNFLAutoDraft({
    teamId: currentTeam?.id ?? null,
    refreshKey: currentPickNumber,
    onError: (message) => toast({
      title: 'Autodraft not changed', description: message, variant: 'destructive',
    }),
  });

  const handleToggleAutoDraft = useCallback(async (next: boolean) => {
    const applied = await setAutoDraft(next);
    if (applied === null) return; // the hook already reported it and resynced
    toast({
      title: applied ? 'Autodraft on' : 'Autodraft off',
      description: applied
        ? 'Your picks will be made for you until you turn this off.'
        : 'You are picking manually again.',
    });
  }, [setAutoDraft, toast]);

  // SAB-459: name/position lookup for the queue panel. availablePlayers already holds the
  // whole draftable pool -- and since SAB-843 it retains drafted players too -- so this
  // needs no extra query. An id with no match renders as a neutral placeholder rather than
  // a blank row.
  const playerById = useMemo(() => {
    const m = new Map<string, AvailablePlayer>();
    for (const p of availablePlayers) m.set(p.id, p);
    return m;
  }, [availablePlayers]);
  const SORT_DEFAULT_DIR: Record<'trash' | 'anti' | 'adp' | 'bye', 'asc' | 'desc'> = {
    trash: 'desc', anti: 'desc', adp: 'asc', bye: 'asc',
  };
  const headerClick = (key: 'trash' | 'anti' | 'adp' | 'bye') => {
    setSort(prev => {
      if (prev?.key !== key) return { key, dir: SORT_DEFAULT_DIR[key] };
      if (prev.dir === SORT_DEFAULT_DIR[key]) {
        return { key, dir: SORT_DEFAULT_DIR[key] === 'desc' ? 'asc' : 'desc' };
      }
      return null;
    });
  };

  // SAB-505: conventional (standard/half_ppr/ppr) leagues have no trash/anti
  // projections, so the board hides those columns and orders by ADP asc (best
  // real player first) instead of the sabotage trash-desc two-group.
  const isConventional = ['standard', 'half_ppr', 'ppr'].includes(currentLeague?.scoring_type ?? '');

  // SAB-848: author display names for draft chat, resolved from nfl_teams at RENDER time.
  //
  // nfl_chat_messages deliberately has no team_name column. Denormalising it would freeze
  // the name at send time, so renaming a team would leave a trail of stale names through
  // the transcript; resolving here relabels the whole history instead. `teams` is already
  // in context, so this costs no query.
  //
  // A message whose author has since left the league resolves to a neutral placeholder
  // rather than an empty span -- an unlabelled line reads as a rendering bug.
  const chatAuthorByUserId = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of teams) if (t.owner_id) map.set(t.owner_id, t.name);
    return map;
  }, [teams]);

  const resolveChatAuthor = useCallback(
    (msg: { user_id: string }) => chatAuthorByUserId.get(msg.user_id) ?? 'Former manager',
    [chatAuthorByUserId],
  );

  // SAB-843: playerId -> name of the team that drafted them.
  //
  // Derived, not fetched. `picks` already carries player_id + team_id and `teamMap`
  // already resolves team names, so this needs no query, no new column and no backend --
  // it is the same data the pick log renders, keyed differently. Mirrors MLB's
  // draftedByTeamMap (useLiveDraft.ts).
  const draftedByTeamMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of picks) {
      if (p.player_id) map.set(p.player_id, teamMap[p.team_id] || 'Unknown');
    }
    return map;
  }, [picks, teamMap]);

  const groupedPlayers = useMemo(() => {
    // SAB-932: position + search + the drafted hide, all through the SHARED helper.
    //
    // The rule it carries is the reconciliation of two shipped tickets that pull opposite
    // ways: SAB-843 (a hunted player must never silently vanish) and SAB-932 (browsing must
    // not be full of unpickable rows). Drafted players are hidden ONLY while browsing; any
    // search shows them, marked. The IDP grouping and the name/position/alias match are
    // ported verbatim into the helper -- see its header and tests.
    //
    // Shared with the V2 room deliberately: while both rooms are live (SAB-931) they must
    // filter identically, and the only way to guarantee that is one implementation.
    const { rows: filteredList } = filterDraftPool({
      players: excludeUnrosterable(availablePlayers, startable),
      draftedIds: new Set(draftedByTeamMap.keys()),
      query: searchQuery,
      posFilter: posFilter as PoolPositionFilter,
      hideDrafted,
      // SAB-998: only consulted when posFilter is FLEX.
      flexPositions: flex,
      // SAB-812: only consulted when posFilter is IDP.
      idpPositions: idp,
      // Legacy rows carry the alias under franchise, not nfl_team.
      teamAliasOf: (pl) => pl.franchise?.alias,
    });
    let list = filteredList;

    if (sort === null) {
      // SAB-505: conventional default = flat ADP asc (best real player first),
      // no dimmed unprojected group.
      if (isConventional) {
        const projected = list
          .slice()
          .sort((a, b) => (a.adp ?? Infinity) - (b.adp ?? Infinity));
        return { projected, unprojected: [] };
      }
      // Sabotage default: TRASH-desc two-group.
      const projected = list
        .filter(p => p.proj_sab_pts != null)
        .sort((a, b) => (b.proj_sab_pts as number) - (a.proj_sab_pts as number));
      const unprojected = list
        .filter(p => p.proj_sab_pts == null)
        .sort((a, b) => (a.adp ?? Infinity) - (b.adp ?? Infinity));
      return { projected, unprojected };
    }

    // Explicit sort: flat list, standard nulls-last (nulls always go to
    // the end regardless of sort direction). ANTI sorts on |stored value|
    // since the column is stored negative but displayed abs() per spec.
    const getVal = (p: AvailablePlayer): number | null => {
      switch (sort.key) {
        case 'trash': return p.proj_sab_pts;
        case 'anti':  return p.projected_anti_points != null ? Math.abs(p.projected_anti_points) : null;
        case 'adp':   return p.adp;
        case 'bye':   return p.franchise?.bye_week_2026 ?? null;
      }
    };
    const sorted = list.slice().sort((a, b) => {
      const aVal = getVal(a);
      const bVal = getVal(b);
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      return sort.dir === 'desc' ? bVal - aVal : aVal - bVal;
    });
    return { projected: sorted, unprojected: [] };
  }, [availablePlayers, posFilter, searchQuery, sort, isConventional, draftedByTeamMap, hideDrafted, startable, flex, idp]);
  const totalCount = groupedPlayers.projected.length + groupedPlayers.unprojected.length;

  // SAB-457 (4): shared row renderer for both groups (projected + unprojected).
  // `dim` is true for the unprojected group to visually de-emphasize per the
  // two-group UX. Sticky-left + sticky-right cells use bg-card opaque so
  // stat cells slide behind on horizontal scroll.
  const renderPlayerRow = (player: AvailablePlayer, dim: boolean) => {
    // SAB-457 (8): spec-faithful row — 34px avatar w/ 1.5px position-colored
    // border, name 14px / 600, line 2 = outlined position pill + team alias.
    const posDraft = getDraftBoardPosColor(player.position);
    const stickyBg = 'bg-card';
    // SAB-843: already taken. The row stays and is marked.
    const draftedByTeam = draftedByTeamMap.get(player.id);
    const isDrafted = draftedByTeam !== undefined;
    return (
      <tr
        key={player.id}
        data-drafted={isDrafted ? 'true' : 'false'}
        className={cn(
          'border-b border-border/30 last:border-b-0',
          dim && 'opacity-60',
          // SAB-749: grey-out PLUS badge for an unavailable player. Still fully
          // draftable — the row is marked, never removed or reordered.
          isUnavailable(player.status) && 'opacity-70',
          // SAB-843: dimmed harder than either of the above, because this one is not a
          // caution — the player is gone and no action on this row can succeed.
          isDrafted && 'opacity-50',
        )}
      >
        {/* Player — sticky-left, opaque */}
        <td className={cn('sticky left-0 z-10 py-2 px-2 w-[208px] min-w-[208px]', stickyBg)}>
          <div className="flex items-center gap-2.5">
            {/* 34px avatar; 1.5px border in spec position color */}
            <div className={cn(
              'h-[34px] w-[34px] rounded-full bg-muted/50 overflow-hidden shrink-0 border-[1.5px]',
              posDraft.border,
            )}>
              {player.headshot_url ? (
                <img src={player.headshot_url} alt={guessWhoActive ? '???' : player.full_name} className="h-full w-full object-cover" loading="lazy" />
              ) : (
                <div className={cn('h-full w-full flex items-center justify-center text-[10px] font-bold', posDraft.text)}>
                  {guessWhoActive ? '?' : getInitials(player.full_name)}
                </div>
              )}
            </div>
            {/* SAB-193: Guess Who masks name + line 2; only headshot remains. */}
            <div className="min-w-0 flex-1 cursor-pointer" onClick={() => !guessWhoActive && setDetailPlayerId(player.id)}>
              {guessWhoActive ? (
                <p className="text-sm font-semibold text-muted-foreground italic leading-tight">???</p>
              ) : (
                <>
                  <p className="text-sm font-semibold text-foreground truncate hover:text-primary transition-colors leading-tight">
                    {player.full_name}
                    {/* SAB-1172: INSIDE the guessWhoActive === false branch, deliberately.
                        DO NOT MOVE THIS OUT OF THE BRANCH.

                        Guess Who (SAB-193) masks the name, the headshot and line two so
                        the target cannot tell who the row is. A news badge is rendered
                        only for players with ACTIVE news — a small, public set (99 across
                        the whole NFL today). Showing it on a masked row therefore narrows
                        the field to "players in the news right now", which is precisely
                        the identifying information the card exists to withhold. The
                        badge's own conditionality is what makes it a leak: an icon that
                        always rendered would say nothing.

                        The same applies to anything else added here that renders
                        conditionally on a player-specific fact. If it distinguishes this
                        row from another masked row, it belongs inside this branch. */}
                    <PlayerNewsIcon
                      sport="nfl"
                      playerId={player.id}
                      playerName={player.full_name}
                      onOpenNews={() => { setDetailTab('news'); setDetailPlayerId(player.id); }}
                      className="inline-flex align-middle ml-1"
                    />
                  </p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    {/* outlined position pill, spec colors */}
                    <span className={cn(
                      'inline-flex items-center px-1.5 py-0 rounded text-[9px] font-bold border whitespace-nowrap',
                      posDraft.text, posDraft.border, posDraft.bgFaint,
                    )}>
                      {player.position}
                    </span>
                    {/* SAB-749: availability marker at the point of decision */}
                    <PlayerAvailabilityBadge status={player.status} size="xs" />
                    {player.franchise?.alias && (
                      <span className="text-[11px] text-muted-foreground">
                        {player.franchise.alias}
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </td>
        {/* TRASH/ANTI — sabotage only. SAB-505: hidden for conventional leagues. */}
        {!isConventional && (
          <>
            {/* TRASH — proj_sab_pts, red */}
            <td className="py-2 px-2 text-right tabular-nums font-display text-damage font-semibold whitespace-nowrap">
              {!guessWhoActive && player.proj_sab_pts != null
                ? Number(player.proj_sab_pts).toFixed(1)
                : <span className="text-muted-foreground">-</span>}
            </td>
            {/* ANTI — stored negative, displayed as abs() in green */}
            <td className="py-2 px-2 text-right tabular-nums font-display text-success font-semibold whitespace-nowrap">
              {!guessWhoActive && player.projected_anti_points != null
                ? Math.abs(Number(player.projected_anti_points)).toFixed(1)
                : <span className="text-muted-foreground">-</span>}
            </td>
          </>
        )}
        {/* ADP — muted */}
        <td className="py-2 px-2 text-right tabular-nums font-display text-muted-foreground whitespace-nowrap">
          {!guessWhoActive && player.adp != null ? player.adp : <span>-</span>}
        </td>
        {/* BYE — muted; non-null guaranteed by SAB-457 (3) for any player with a franchise */}
        <td className="py-2 px-2 text-right tabular-nums font-display text-muted-foreground whitespace-nowrap">
          {!guessWhoActive && player.franchise?.bye_week_2026 != null ? player.franchise.bye_week_2026 : <span>-</span>}
        </td>
        {/* Actions — sticky-right, opaque. SAB-457 (9): Queue toggle +
            Draft button per spec. Queue is per-session local state; the
            right-rail queue list + drag-reorder + auto-pick wiring live
            on SAB-459. */}
        <td className={cn('sticky right-0 z-10 py-2 px-2 w-[150px] min-w-[150px]', stickyBg)}>
          {/* SAB-843: for a taken player the buttons are replaced, not merely disabled.
              A greyed "Draft" button still reads as "maybe later"; naming the team that
              took them answers the actual question a manager has. */}
          {isDrafted ? (
            <div className="flex items-center justify-end">
              <span
                className="text-[10px] font-semibold text-muted-foreground truncate"
                title={`Drafted by ${draftedByTeam}`}
              >
                Drafted by {draftedByTeam}
              </span>
            </div>
          ) : (
          <div className="flex items-center justify-end gap-1.5">
            <Button
              size="sm"
              variant="outline"
              onClick={() => toggleQueue(player.id)}
              aria-pressed={queueSet.has(player.id)}
              aria-label={queueSet.has(player.id) ? 'Remove from queue' : 'Add to queue'}
              className={cn(
                'h-7 px-2 text-[10px] font-bold',
                queueSet.has(player.id)
                  ? 'text-success border-success/40 bg-success/10 hover:bg-success/15'
                  : 'border-border text-muted-foreground hover:text-foreground',
              )}
            >
              {queueSet.has(player.id)
                ? <><Check className="h-3 w-3 mr-1" aria-hidden="true" />Queued</>
                : <><Plus className="h-3 w-3 mr-1" aria-hidden="true" />Queue</>}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2 text-[10px] font-bold border-primary/20 text-primary hover:bg-primary/10"
              onClick={() => handlePick(player.id)}
              disabled={isPicking}
            >
              {isPicking ? '...' : 'Draft'}
            </Button>
          </div>
          )}
        </td>
      </tr>
    );
  };

  // Group picks by round
  const madePicks = useMemo(() => picks.filter(p => p.player_id), [picks]);
  const byRound = useMemo(() => {
    const groups: Record<number, DraftPick[]> = {};
    for (const p of madePicks) {
      if (!groups[p.round]) groups[p.round] = [];
      groups[p.round].push(p);
    }
    return Object.entries(groups)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([r, ps]) => ({ round: Number(r), picks: ps }));
  }, [madePicks]);

  // SAB-842: the shared DraftRoster/DraftBoard components speak DraftPickLog, so adapt
  // once here rather than at each call site.
  const pickLogs = useMemo<DraftPickLog[]>(() => madePicks.map(p => ({
    pickNumber: p.pick_number,
    round: p.round,
    pickInRound: p.pick_in_round,
    teamId: p.team_id,
    teamName: teamMap[p.team_id] || 'Unknown',
    playerId: p.player_id as string,
    playerName: p.player_name || 'Unknown',
    position: p.player_position || '?',
    timestamp: new Date(),
    wasAutoPick: p.auto_picked,
    wasCommissionerPick: p.picked_by_commissioner,
    // SAB-847: optional on DraftPickLog; MLB leaves both undefined.
    nflTeam: p.player_nfl_team,
    byeWeek: p.player_bye_week,
  })), [madePicks, teamMap]);

  // Every team, always -- including ones with no picks yet.
  //
  // SAB-842: this used to end in `.filter(g => g.picks.length > 0)`. That was survivable
  // in a completed-draft view, where every team has picks by definition. The moment the
  // view became reachable DURING a draft it turns into a real defect: in round 1 only the
  // teams that have already picked exist at all, so a manager looking for their own team
  // early on simply cannot find it. Teams are a fixed roster, not a result set.
  const teamsForRoster = useMemo(
    () => teams.map(t => ({ id: t.id, name: t.name })),
    [teams],
  );

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    if (m > 59) {
      const h = Math.floor(m / 60);
      const rm = m % 60;
      return `${h}h ${rm}m`;
    }
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  if (contextLoading || isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!currentLeague) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <Gavel className="h-10 w-10 text-muted-foreground mb-4" />
        <p className="text-sm text-muted-foreground">Join a league to access the draft</p>
      </div>
    );
  }

  // ── Pre-Draft: Commissioner can start ──
  // SAB-534 Chunk 2: commissioner pause / resume (unified state, one action)
  const handlePauseResume = async (action: 'pause' | 'resume') => {
    if (!currentLeague) return;
    setPauseBusy(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const res = await fetch(
        `${(supabase as any).supabaseUrl}/functions/v1/nfl-pause-draft`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ league_id: currentLeague.id, action }),
        },
      );
      const result = await res.json();
      if (result.error) {
        toast({ title: action === 'pause' ? 'Pause failed' : 'Resume failed', description: result.error, variant: 'destructive' });
      } else {
        toast({
          title: action === 'pause' ? 'Draft paused' : 'Draft resumed',
          description: action === 'pause'
            ? `Clock stopped with ${result.remaining_seconds ?? 0}s remaining.`
            : 'Clocks are running again.',
        });
      }
      await fetchDraftState();
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setPauseBusy(false);
    }
  };

  // SAB-534 Chunk 3: commissioner undo last pick + cancel draft (confirm-gated)
  const callDraftAdmin = async (fn: 'nfl-undo-pick' | 'nfl-cancel-draft') => {
    if (!currentLeague) return;
    setUndoBusy(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const res = await fetch(
        `${(supabase as any).supabaseUrl}/functions/v1/${fn}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ league_id: currentLeague.id }),
        },
      );
      const result = await res.json();
      if (result.error) {
        toast({ title: fn === 'nfl-undo-pick' ? 'Undo failed' : 'Cancel failed', description: result.error, variant: 'destructive' });
      } else if (fn === 'nfl-undo-pick') {
        toast({ title: 'Pick undone', description: `${result.player_name} removed. ${result.team_name} is back on the clock.` });
      } else {
        toast({ title: 'Draft canceled', description: `${result.cleared_picks} picks erased. The draft can be restarted.` });
      }
      await fetchDraftState();
      await fetchPicks();
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setUndoBusy(false);
    }
  };

  const handleUndoLastPick = () => {
    const lastFilled = [...picks].filter(p => p.player_id).sort((a, b) => b.pick_number - a.pick_number)[0];
    const label = lastFilled
      ? `Undo R${lastFilled.round}.${lastFilled.pick_in_round}: ${lastFilled.player_name || 'this pick'} (${teamMap[lastFilled.team_id] || 'team'})?`
      : 'Undo the last pick?';
    if (!window.confirm(`${label}

The pick is erased, the player returns to the pool, and that team goes back on the clock.`)) return;
    callDraftAdmin('nfl-undo-pick');
  };

  const handleCancelDraft = () => {
    const filled = picks.filter(p => p.player_id).length;
    if (!window.confirm(`Cancel this draft? This ERASES all ${filled} picks and all drafted rosters.

The league returns to pre-draft and the draft can be restarted from scratch. This cannot be undone.`)) return;
    callDraftAdmin('nfl-cancel-draft');
  };

  // SAB-534 Chunk 4: fill every remaining slot via auto-pick. The edge fn
  // fills a chunk per call (server time budget); we re-invoke until done.
  // All state lives in the draft tables, so this is fully resumable — a
  // reload / re-trigger continues from the current pointer, and the atomic
  // lock guarantees no double-picks if two loops race.
  const handleAutoDraftAll = async () => {
    if (!currentLeague) return;
    const remaining = picks.filter(p => !p.player_id).length;
    if (!window.confirm(`Auto-draft the remaining ${remaining} pick${remaining === 1 ? '' : 's'}?

Every open slot is filled by auto-pick for its team. This CANNOT be bulk-undone — undo only reverses the single most recent pick.`)) return;

    autoDraftCancelRef.current = false;
    setAutoDraftAllBusy(true);
    setAutoDraftProgress('Starting…');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      let guard = 0;
      while (guard++ < 100) {
        if (autoDraftCancelRef.current) {
          toast({ title: 'Auto-draft stopped', description: 'The draft returns to normal on-the-clock picking.' });
          break;
        }
        const res = await fetch(
          `${(supabase as any).supabaseUrl}/functions/v1/nfl-auto-draft-all`,
          {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ league_id: currentLeague.id }),
          },
        );
        const result = await res.json();
        if (result.error) {
          toast({ title: 'Auto-draft failed', description: result.error, variant: 'destructive' });
          break;
        }
        await fetchPicks();
        await fetchDraftState();
        if (result.done) {
          setAutoDraftProgress('Draft complete');
          toast({ title: '🏆 Draft filled', description: `All remaining picks auto-drafted (${result.total_picks} total).` });
          break;
        }
        // No progress and a reason (paused / sabotage wall / no players) — stop.
        if (result.picks_made === 0 && result.stop_reason) {
          toast({ title: 'Auto-draft stopped', description: result.stop_reason, variant: 'destructive' });
          break;
        }
        setAutoDraftProgress(`Pick ${result.current_pick} of ${result.total_picks}…`);
        // Only a concurrent drafter yields picks_made=0 with no reason; back
        // off briefly so we don't hammer the lock.
        if (result.picks_made === 0) await new Promise(r => setTimeout(r, 400));
      }
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setAutoDraftAllBusy(false);
      setAutoDraftProgress('');
      autoDraftCancelRef.current = false;
      await fetchPicks();
      await fetchDraftState();
    }
  };

  // ── Draft views: By Round / By Team / Recap ────────────────────────────────
  //
  // SAB-842: hoisted out of the completed-draft return so BOTH branches can render it.
  //
  // These renderers were always written to work mid-draft -- their own empty states say
  // "Draft results will appear here once picks are made" and "Picks will appear here as
  // the draft progresses", copy that can only appear when the draft is NOT complete. They
  // were simply unreachable: the in_progress/paused branch returns before this point, so
  // the live case the copy was written for never rendered.
  //
  // Hoisting rather than deleting that early return, because the live branch also carries
  // the on-the-clock banner, the player pool and the pick controls. Removing it outright
  // would have made the board reachable by taking the draft room away.
  // SAB-845 / SAB-847: an order-rewriting sabotage makes every UNPICKED slot in
  // nfl_draft_picks name the wrong team, because nfl-make-pick resolves the effective
  // picker lazily and rewrites only the row being picked. Hoisted here because BOTH the
  // order strip (which hides itself) and the draft board (which blanks unpicked cells)
  // need it, and the strip lives in the live-draft branch while the board lives in the
  // shared views section below.
  const orderScrambled = activeEffects.some(
    e => e.active && (e.effect_type === 'reverse_order' || e.effect_type === 'skip_pick'),
  );

  const draftViewsSection = (
    <>
        {/* By Round / By Team / Recap toggle */}
        <div className="flex items-center gap-1 p-1 bg-muted/30 rounded-lg w-fit">
          {([['round', 'By Round'], ['team', 'By Team'], ['board', 'Board'], ['recap', 'Recap']] as const).map(([v, label]) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={cn(
                'px-4 py-1.5 rounded-md text-xs font-semibold transition-all',
                view === v
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {label}
            </button>
          ))}
        </div>

        {view === 'board' ? (
          /* SAB-847: Sleeper-style board — rounds down, teams across. DraftBoard is shared
             with MLB and presentational; NFL opts into the richer cell and supplies its own
             palette rather than growing a second one inside that file. `expanded` is passed
             unconditionally so the grid is horizontally scrollable at every width: with 10
             teams the default flex-1 columns collapse to roughly 31px on a phone, which
             fits neither a name nor the four fields below it. */
          <div className="space-y-3">
            {orderScrambled && (
              <div
                data-testid="board-order-scrambled"
                className="rounded-lg border border-purple-500/40 bg-purple-900/20 p-3 text-center"
              >
                <p className="text-purple-300 font-bold text-sm">Pick order is being rewritten</p>
                <p className="text-purple-400/90 text-xs mt-0.5">
                  A sabotage is changing this round&apos;s order. Picks already made are
                  accurate; upcoming slots are left blank rather than shown wrong.
                </p>
              </div>
            )}
            <DraftBoard
              picks={pickLogs}
              teams={teams.map(t => ({ id: t.id, name: t.name, owner: '' }))}
              totalRounds={picks.length > 0 ? Math.max(...picks.map(p => p.round)) : 0}
              currentPick={currentPickNumber}
              viewMode="grid"
              draftOrder={picks.map(p => ({
                id: p.id,
                team_id: p.team_id,
                round: p.round,
                pick_number: p.pick_number,
                round_pick: p.pick_in_round,
              }))}
              fillHeight={false}
              expanded
              detailedCells
              blankUnpicked={orderScrambled}
              colorFor={getBoardCellColor}
            />
          </div>
        ) : view === 'recap' ? (
          <DraftRecap picks={madePicks} teams={teams} teamMap={teamMap} myTeamId={currentTeam?.id} />
        ) : view === 'round' ? (
          <div className="space-y-3">
            {byRound.length === 0 && (
              <Card className="bg-card border-border">
                <CardContent className="py-10 text-center space-y-2">
                  <Gavel className="h-8 w-8 text-muted-foreground mx-auto" />
                  <p className="text-sm font-medium text-foreground">No draft picks yet</p>
                  <p className="text-xs text-muted-foreground">
                    {draftStatus === 'completed'
                      ? "Draft completed but no picks loaded. Try refreshing."
                      : "Draft results will appear here once picks are made."}
                  </p>
                  <Button size="sm" variant="outline" onClick={() => fetchPicks()}>
                    Refresh
                  </Button>
                </CardContent>
              </Card>
            )}
            {byRound.map(({ round, picks: rPicks }) => (
              <Card key={round} className="bg-card border-border overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2 bg-muted/20 border-b border-border">
                  <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                    Round {round}
                  </span>
                  <span className="text-[10px] text-muted-foreground">{rPicks.length} picks</span>
                </div>
                <CardContent className="p-0">
                  {rPicks.map(pick => (
                    <PickRow
                      key={pick.id}
                      pick={pick}
                      teamName={teamMap[pick.team_id] || '?'}
                      isMyPick={pick.team_id === currentTeam?.id}
                      showTeam
                      masked={isPickMasked(pick.pick_number)}
                      onPlayerClick={setDetailPlayerId}
                    />
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          /* SAB-842: By Team is now a real ROSTER view, not a pick list.
             DraftRoster is shared with MLB and purely presentational, so it is reused
             rather than forked: NFL's 19 expanded slots go in through rosterConfig, and
             the slot each player actually occupies comes from nfl_rosters via
             slotAssignments -- the server's own answer, never re-derived here. */
          <DraftRoster
            picks={pickLogs}
            players={[]}
            teams={teamsForRoster}
            myTeamId={currentTeam?.id ?? ''}
            rosterConfig={{ slot_order: rosterSlotOrder }}
            slotAssignments={slotByPlayerId}
          />
        )}
    </>
  );


  if (draftStatus === 'pending' || draftStatus === null) {
    return (
      <div className="space-y-4 pb-20 md:pb-0">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-display font-bold text-foreground flex items-center gap-2">
            <Gavel className="h-5 w-5 text-primary" />
            NFL Draft
          </h1>
        </div>
        <Card className="bg-card border-border">
          <CardContent className="p-6 text-center space-y-4">
            <div className="h-14 w-14 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto">
              <Play className="h-7 w-7 text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">Draft Not Started</p>
              <p className="text-xs text-muted-foreground mt-1">
                {teams.length} teams ready · Snake draft · {DEFAULT_DRAFT_ROUNDS} rounds
              </p>
            </div>
            {isCommissioner ? (
              <>
                {commissionerUnpaid && (
                  <p className="text-xs text-amber-400">
                    Pay your entry fee first — your ${commissionerFee.entryFee} buy-in unlocks the draft. Pay it from the league lobby.
                  </p>
                )}
                <Button
                  onClick={handleStartDraft}
                  disabled={isPicking || teams.length < 2 || commissionerUnpaid}
                  className="bg-primary hover:bg-primary/90"
                >
                  {isPicking ? <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
                  Start Draft
                </Button>
              </>
            ) : (
              <p className="text-xs text-muted-foreground">Waiting for commissioner to start the draft</p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Live Draft Room ──
  if (draftStatus === 'in_progress' || draftStatus === 'paused') {
    // Paused if EITHER signal says so -- see the draftPausedAt declaration above.
    const isPaused = draftStatus === 'paused' || !!draftPausedAt;
    const onClockTeamName = onTheClockTeamId ? (teamMap[onTheClockTeamId] || 'Unknown') : '-';
    const currentRound = picks.find(p => !p.player_id)?.round || 1;

    // ── SAB-844: DraftOrderStrip adapter ────────────────────────────────────
    // The strip is shared with MLB and purely presentational (zero DB access), so it is
    // reused rather than rebuilt. Everything below is a shape map from data already on
    // this page. Three mismatches to bridge:
    //
    // 1. FIELD NAMES. NFL's DraftSabotageEffect carries `active` + `pick_number`; the
    //    strip wants `status` + `applied_at_pick`.
    // 2. EFFECT VOCABULARY. The two sports named the same effects differently -- NFL
    //    'skip_pick' vs the strip's 'pick_skip' (not a typo, genuinely reversed),
    //    'timer_half' vs 'timer_reduction', and NFL splits Guess Who into
    //    'league_guess_who' / 'hide_info' where the strip has one 'guess_who'. Left
    //    unmapped these silently do nothing: the strip would render no markers and look
    //    like it simply had no sabotages to show.
    // 3. NULL TARGETS. NFL league-wide effects carry target_team_id = null; the strip
    //    keys per team, so those are dropped rather than coerced.
    //
    // NOTE: the skip_pick -> pick_skip line is currently UNREACHABLE, and deliberately
    // kept. A live skip_pick trips the orderScrambled bail-out below, which hides the
    // strip entirely, so its marker can never draw. It stays because it is the correct
    // mapping and costs nothing -- if the bail-out is ever narrowed, the marker should
    // work rather than silently not. It is the one line here with no test behind it.
    const stripSabotages = activeEffects
      .filter(e => e.target_team_id !== null)
      .map(e => ({
        id: e.id,
        target_team_id: e.target_team_id as string,
        effect_type:
          e.effect_type === 'skip_pick' ? 'pick_skip'
          : e.effect_type === 'timer_half' ? 'timer_reduction'
          : (e.effect_type === 'league_guess_who' || e.effect_type === 'hide_info') ? 'guess_who'
          : e.effect_type,
        status: e.active ? 'active' : 'expired',
        applied_at_pick: e.pick_number ?? 0,
        expires_at_pick: e.expires_at_pick,
      }));

    // ORDER-REWRITING EFFECTS MAKE THIS STRIP UNTRUSTWORTHY, so it is not shown.
    //
    // nfl_draft_picks stores the BASE snake order. nfl-make-pick resolves the effective
    // picker only at the instant a pick is made and rewrites that one row -- its own
    // comment: "we do NOT pre-rewrite future picks in the round". So while a
    // reverse_order or skip_pick is live, every UNPICKED slot in this table still names
    // the team that would have picked without the sabotage.
    //
    // A "who's up next" strip that confidently lists the wrong teams is worse than no
    // strip: it is the one component whose entire value is being right about upcoming
    // order, and the sabotage that breaks it is invisible to the reader. Correcting it
    // client-side would mean a second copy of computeRoundOrder in the browser -- the
    // divergent-duplicate mistake SAB-739 deleted -- so instead the strip steps aside and
    // says why. Already-made slots are unaffected (those rows were rewritten at pick
    // time), but partial truth is not worth the ambiguity here.
    const stripOrder = picks.map(p => ({
      id: p.id,
      team_id: p.team_id,
      round: p.round,
      pick_number: p.pick_number,
      round_pick: p.pick_in_round,
    }));

    // NFL has no skipped-pick concept to feed this. Verified against prod rather than
    // assumed: nfl_draft_picks has no is_skipped column (neither does MLB's draft_picks
    // -- that read is vestigial on both), and NFL's skip_pick does not mark a pick at
    // all. It ROTATES the target to the end of the round (_shared/nfl-draft-order.ts:
    // "move target_team_id from their current slot to the END"). Nobody is passed over,
    // so no pick number is ever "skipped" -- and the case where order changes is exactly
    // the case handled by orderScrambled above.
    const stripSkipped: Set<number> = new Set();

    return (
      <div className={cn('space-y-3 pb-20 md:pb-0', screenShake && 'animate-screen-shake')}>
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-display font-bold text-foreground flex items-center gap-2">
            <Gavel className="h-5 w-5 text-primary" />
            Live Draft
          </h1>
          <div className="flex items-center gap-2">
            {isCommissioner && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={undoBusy || !picks.some(p => p.player_id)}
                  onClick={handleUndoLastPick}
                  className="h-7 px-2.5 border-border text-muted-foreground hover:text-foreground"
                  aria-label="Undo last pick"
                >
                  ↩ Undo
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={undoBusy || autoDraftAllBusy}
                  onClick={handleCancelDraft}
                  className="h-7 px-2.5 border-red-900/60 text-red-400 hover:bg-red-950/40"
                  aria-label="Cancel draft"
                >
                  🛑 Cancel
                </Button>
                {autoDraftAllBusy ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { autoDraftCancelRef.current = true; }}
                    className="h-7 px-2.5 border-amber-500/50 text-amber-400 hover:bg-amber-950/30 tabular-nums"
                    aria-label="Stop auto-draft"
                    title={autoDraftProgress || 'Stopping…'}
                  >
                    ⏹ Stop{autoDraftProgress ? ` · ${autoDraftProgress}` : ''}
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={undoBusy || isPaused || !picks.some(p => !p.player_id)}
                    onClick={handleAutoDraftAll}
                    className="h-7 px-2.5 border-amber-500/40 text-amber-400 hover:bg-amber-950/30"
                    aria-label="Auto-draft remaining picks"
                    title={isPaused ? 'Resume the draft first' : 'Auto-draft all remaining picks'}
                  >
                    ⚡ Auto-draft all
                  </Button>
                )}
              </>
            )}
            {isCommissioner && (
              <Button
                size="sm"
                variant="outline"
                disabled={pauseBusy}
                onClick={() => handlePauseResume(isPaused ? 'resume' : 'pause')}
                className="h-7 px-2.5 border-border text-muted-foreground hover:text-foreground"
                aria-label={isPaused ? 'Resume draft' : 'Pause draft'}
              >
                {isPaused ? '▶ Resume' : '⏸ Pause'}
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => setSabotagePanelOpen(true)}
              className="h-7 px-2.5 border-primary/30 text-primary hover:bg-primary/10"
              aria-label="Open draft sabotage panel"
            >
              <Bomb className="h-3.5 w-3.5 mr-1.5" />
              Sabotage
            </Button>
            <Badge variant="outline" className="text-[10px] border-border text-muted-foreground">
              Pick {currentPickNumber} of {totalPicks}
            </Badge>
          </div>
        </div>

        {isPaused && (
          <div className="bg-sky-900/30 border border-sky-500/50 rounded-lg p-3 text-center">
            <span className="text-2xl">⏸️</span>
            <p className="text-sky-300 font-bold text-sm">
              DRAFT PAUSED
              {pauseReason ? `, ${pauseReason === 'commissioner' ? 'by the commissioner' : pauseReason === 'overnight' ? 'overnight window' : 'inactivity'}` : ''}
            </p>
            {pausedRemaining != null && (
              <p className="text-sky-400/80 text-xs mt-1 tabular-nums">
                {Math.floor(pausedRemaining / 60)}:{String(pausedRemaining % 60).padStart(2, '0')} will be on the clock at resume
              </p>
            )}
          </div>
        )}

        {/* SAB-193: League-wide effect banners */}
        {snakeReversalActive && (
          <div className="bg-amber-900/30 border border-amber-500/50 rounded-lg p-3 text-center">
            <span className="text-2xl">🐍</span>
            <p className="text-amber-300 font-bold text-sm">
              SNAKE REVERSAL: Round {snakeReversalActive.expires_at_round} order is reversed
            </p>
            {/* SAB-450 (4): countdown */}
            <p className="text-amber-400/80 text-xs mt-1 tabular-nums">
              {describeEffectExpiry(snakeReversalActive)}
            </p>
          </div>
        )}
        {roughingActive && (
          <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-3 text-center animate-pulse">
            <span className="text-2xl">💥</span>
            <p className="text-red-300 font-bold text-sm">
              ROUGHING THE DRAFTER: Everyone is in Guess Who mode this round
            </p>
            {/* SAB-450 (4): countdown */}
            <p className="text-red-400/80 text-xs mt-1 tabular-nums">
              {describeEffectExpiry(roughingActive)}
            </p>
          </div>
        )}

        {/* SAB-193: Fake News injury banner (only the target sees this) */}
        {fakeNewsEffect && (
          <div className="bg-red-900/40 border border-red-500/60 rounded-lg p-3 animate-pulse">
            <div className="flex items-start gap-3">
              <span className="text-2xl shrink-0">🚨</span>
              <div className="min-w-0">
                <p className="text-red-400 font-bold text-[10px] uppercase tracking-wider">Breaking News</p>
                <p className="text-white font-semibold text-sm leading-tight">
                  {fakeNewsEffect.effect_data?.player_name || 'Star player'} ruled OUT: {fakeNewsEffect.effect_data?.injury || 'undisclosed injury'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* On The Clock Banner */}
        <Card className={cn(
          'border overflow-hidden',
          isMyTurn
            ? 'bg-gradient-to-r from-primary/10 to-primary/5 border-primary/30'
            : 'bg-gradient-to-r from-muted/30 to-card border-border'
        )}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={cn(
                  'h-10 w-10 rounded-xl flex items-center justify-center shrink-0',
                  isMyTurn ? 'bg-primary/20 border border-primary/30' : 'bg-muted/50 border border-border'
                )}>
                  <Clock className={cn('h-5 w-5', isMyTurn ? 'text-primary' : 'text-muted-foreground')} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">On The Clock</p>
                  <p className={cn('text-sm font-bold', isMyTurn ? 'text-primary' : 'text-foreground')}>
                    {isMyTurn ? 'Your Pick!' : onClockTeamName}
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    Round {currentRound} · Pick {currentPickNumber}
                  </p>
                </div>
              </div>
              {timeLeft !== null && (
                <div className="flex items-center gap-2">
                  <div className={cn(
                    'text-2xl font-bold tabular-nums',
                    timeLeft <= 30 ? 'text-red-400 animate-pulse' : isMyTurn ? 'text-primary' : 'text-foreground'
                  )}>
                    {formatTime(timeLeft)}
                  </div>
                  {/* Delay of Game indicator — server has already halved the
                      deadline; this just tells the user why their clock is
                      shorter than usual. */}
                  {delayOfGameOnMe && isMyTurn && (
                    <span className="text-[10px] text-red-400 animate-pulse font-bold">
                      ⏱️ HALVED
                    </span>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* SAB-840 / SAB-853: autodraft state + control.
            Sits directly under the clock, not in a settings menu. The server can turn this
            ON without the user acting -- letting a clock lapse enables it -- so a manager
            must be able to discover that fact at the moment it starts costing them picks.
            Anything buried fails that test.

            `paused` is included deliberately: a pause is the one calm moment a flagged
            manager has to clear the flag before their picks resume being taken. */}
        {currentTeam && (draftStatus === 'in_progress' || draftStatus === 'paused') && (
          <div
            data-testid="autodraft-banner"
            data-autodraft={autoDraftOn === null ? 'unknown' : String(autoDraftOn)}
            className={cn(
              'rounded-lg border p-3 flex items-center justify-between gap-3',
              autoDraftOn
                ? 'bg-amber-900/30 border-amber-500/50'
                : 'bg-muted/30 border-border',
            )}
          >
            <div className="flex items-center gap-3 min-w-0">
              <Bomb className={cn('h-5 w-5 shrink-0', autoDraftOn ? 'text-amber-400' : 'text-muted-foreground')} />
              <div className="min-w-0">
                <p className={cn('text-sm font-bold', autoDraftOn ? 'text-amber-300' : 'text-foreground')}>
                  {autoDraftOn === null
                    ? 'Checking autodraft…'
                    : autoDraftOn ? 'Autodraft is ON' : 'Autodraft is off'}
                </p>
                <p className={cn('text-xs', autoDraftOn ? 'text-amber-400/90' : 'text-muted-foreground')}>
                  {autoDraftOn === null
                    ? "We could not read your autodraft setting. You can still turn it off."
                    : autoDraftOn
                      ? 'Your picks are being made automatically until you turn it off.'
                      : 'You are picking manually. If your clock runs out, autodraft turns on.'}
                </p>
              </div>
            </div>
            {/* Offered whenever the flag is not known to be off, so an unreadable flag never
                traps a manager with no way out. */}
            <Button
              size="sm"
              variant={autoDraftOn === false ? 'outline' : 'default'}
              disabled={autoDraftSaving}
              onClick={() => handleToggleAutoDraft(!autoDraftOn)}
              className="shrink-0"
            >
              {autoDraftSaving ? 'Saving…' : autoDraftOn === false ? 'Turn on' : 'Turn off'}
            </Button>
          </div>
        )}

        {/* SAB-844: pick order — "when do I pick next", the question asked every pick.
            Sits under the clock because it is read together with it. */}
        {orderScrambled ? (
          <div
            data-testid="order-scrambled"
            className="rounded-lg border border-purple-500/40 bg-purple-900/20 p-3 text-center"
          >
            <p className="text-purple-300 font-bold text-sm">Pick order is being rewritten</p>
            <p className="text-purple-400/90 text-xs mt-0.5">
              A sabotage is changing this round&apos;s order, so the upcoming list is hidden
              rather than shown wrong. Watch the clock above for who is actually up.
            </p>
          </div>
        ) : (
          /* draft_pick_timer / draft_type are real nfl_leagues columns that the context's
             NFLLeague type has not been regenerated to include, hence the casts below;
             cast at the call site rather than widening a shared type from here. */
          <DraftOrderStrip
            draftOrder={stripOrder}
            teams={teams.map(t => ({ id: t.id, name: t.name, owner: '' }))}
            currentPick={currentPickNumber}
            currentRound={currentRound}
            totalRounds={picks.length > 0 ? Math.max(...picks.map(p => p.round)) : 0}
            timeRemaining={timeLeft ?? 0}
            totalTime={(currentLeague as any)?.draft_pick_timer ?? 60}
            isPaused={isPaused}
            isSlowDraft={(currentLeague as any)?.draft_type === 'slow'}
            overnightPauseActive={pauseReason === 'overnight'}
            pauseEndHour={0}
            activeSabotages={stripSabotages}
            skippedPickNumbers={stripSkipped}
            myTeamId={currentTeam?.id}
          />
        )}

        {/* SAB-193: Guess Who banner — shown to the target while their info is hidden */}
        {guessWhoActive && (
          <div className="bg-purple-900/30 border border-purple-500/50 rounded-lg p-3 text-center animate-pulse">
            <span className="text-2xl">🎭</span>
            <p className="text-purple-300 font-bold text-sm">GUESS WHO is active</p>
            <p className="text-purple-400 text-xs">You can only see headshots. Good luck.</p>
            {/* SAB-450 (4): countdown — soonest-ending mask source */}
            {guessWhoExpiryText && (
              <p className="text-purple-400/80 text-xs mt-1 tabular-nums">
                {guessWhoExpiryText}
              </p>
            )}
          </div>
        )}

        {/* Player Search & Pick (when it's your turn or commissioner) */}
        {(isMyTurn || isCommissioner) && !isPaused && (
          <Card className="bg-card border-border">
            <CardContent className="p-3 space-y-2">
              {/* Search + Position filter — SAB-457 (7): substring filter
                  matches name OR position OR team alias. Clear (✕) button
                  appears while non-empty. */}
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    placeholder="Search name, team, or position…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-8 pl-8 pr-7 text-xs bg-muted/30"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      aria-label="Clear search"
                      className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 inline-flex items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    >
                      <X className="h-3 w-3" aria-hidden="true" />
                    </button>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-1">
                {positionFilters.map(pos => (
                  <button
                    key={pos}
                    onClick={() => setPosFilter(pos)}
                    className={cn(
                      'px-2 py-0.5 rounded text-[10px] font-bold transition-colors',
                      posFilter === pos
                        ? 'bg-primary/10 text-primary border border-primary/20'
                        : 'text-muted-foreground hover:text-foreground border border-transparent'
                    )}
                  >
                    {pos}
                  </button>
                ))}

                {/* SAB-950: was a pill in this row and read as an eleventh position
                    filter. Now the shared switch treatment (SAB-881), so it reads as a
                    control rather than another thing the list is filtered by. Behaviour is
                    unchanged -- filterDraftPool still owns it. */}
                <HideDraftedControl
                  hidden={hideDrafted}
                  onChange={() => toggleHideDrafted()}
                  testId="hide-drafted-toggle"
                  switchTestId="hide-drafted-switch"
                  className="ml-1"
                />
              </div>

              {/* SAB-457 (6): inversion-coloring legend.
                  SAB-457 (9): queue count surfaces inline so users get
                  feedback that the toggle persists (right-rail queue list
                  ships with SAB-459). */}
              <div className="flex items-center justify-between gap-2 px-1 py-1">
                <p className="text-[10px] text-muted-foreground">
                  {isConventional
                    ? <>Ordered by <span className="font-bold">ADP</span> (best available first). BYE = context.</>
                    : <><span className="text-damage font-bold">TRASH</span> + <span className="text-success font-bold">ANTI</span> score for you (inverted). ADP / BYE = context.</>}
                </p>
                {queue.length > 0 && (
                  <span className="text-[10px] font-bold text-success bg-success/10 border border-success/30 rounded-full px-2 py-0.5 whitespace-nowrap">
                    Queue ({queue.length})
                  </span>
                )}
              </div>

              {/* Player pool — SAB-457 (4): 6-column stat table; (5): click sort. */}
              <div className="max-h-[280px] overflow-auto">
                {playersLoading ? (
                  <div className="py-8 text-center text-xs text-muted-foreground">Loading players...</div>
                ) : totalCount === 0 ? (
                  // SAB-457 (7): distinct empty-state message when search is active
                  // so the user knows the filter is the reason, not that nothing loaded.
                  <div className="py-8 text-center text-xs text-muted-foreground">
                    {searchQuery.trim() ? `No players match "${searchQuery.trim()}"` : 'No players found'}
                  </div>
                ) : (
                  <ActiveNewsProvider sport="nfl" keys={[...groupedPlayers.projected, ...groupedPlayers.unprojected].map(p => p.id)}>
                  <table className="w-full text-xs min-w-[680px]">
                    <thead className="sticky top-0 bg-card z-20">
                      <tr className="border-b border-border/30">
                        <th className="sticky left-0 bg-card z-30 text-left py-2 px-2 font-bold text-[10px] uppercase tracking-[0.05em] text-muted-foreground w-[208px] min-w-[208px]">
                          Player
                        </th>
                        {/* SAB-457 (5): sortable stat headers. 3-state click cycle
                            (inactive → default dir → flipped → cleared). ARIA
                            aria-sort reflects active key + direction. */}
                        {(isConventional
                          ? ([
                              { key: 'adp', label: 'ADP', color: 'text-muted-foreground' },
                              { key: 'bye', label: 'BYE', color: 'text-muted-foreground' },
                            ] as const)
                          : ([
                              { key: 'trash', label: 'TRASH', color: 'text-damage' },
                              { key: 'anti',  label: 'ANTI',  color: 'text-success' },
                              { key: 'adp',   label: 'ADP',   color: 'text-muted-foreground' },
                              { key: 'bye',   label: 'BYE',   color: 'text-muted-foreground' },
                            ] as const)
                        ).map(col => {
                          const isActive = sort?.key === col.key;
                          const ariaSort: 'ascending' | 'descending' | 'none' =
                            isActive ? (sort!.dir === 'desc' ? 'descending' : 'ascending') : 'none';
                          return (
                            <th
                              key={col.key}
                              aria-sort={ariaSort}
                              className={cn(
                                'text-right py-2 px-2 font-bold text-[10px] uppercase tracking-[0.05em] whitespace-nowrap',
                                col.color,
                              )}
                            >
                              <button
                                type="button"
                                onClick={() => headerClick(col.key)}
                                className="inline-flex items-center justify-end gap-1 w-full text-right cursor-pointer select-none hover:opacity-80 transition-opacity"
                              >
                                {col.label}
                                {isActive && (sort!.dir === 'desc'
                                  ? <ChevronDown className="h-3 w-3" aria-hidden="true" />
                                  : <ChevronUp className="h-3 w-3" aria-hidden="true" />
                                )}
                              </button>
                            </th>
                          );
                        })}
                        <th className="sticky right-0 bg-card z-30 py-2 px-2 w-[150px] min-w-[150px]" aria-label="Actions" />
                      </tr>
                    </thead>
                    {/* Group A — projected */}
                    <tbody>
                      {groupedPlayers.projected.map(player => renderPlayerRow(player, false))}
                    </tbody>
                    {/* Divider — only when both groups have content */}
                    {groupedPlayers.projected.length > 0 && groupedPlayers.unprojected.length > 0 && (
                      <tbody>
                        <tr>
                          <td colSpan={6} className="py-2 text-center text-[10px] uppercase tracking-[0.05em] text-muted-foreground italic border-t border-border/50">
                            no trash projection ({groupedPlayers.unprojected.length} player{groupedPlayers.unprojected.length === 1 ? '' : 's'})
                          </td>
                        </tr>
                      </tbody>
                    )}
                    {/* Group B — unprojected, dimmed */}
                    <tbody>
                      {groupedPlayers.unprojected.map(player => renderPlayerRow(player, true))}
                    </tbody>
                  </table>
                  </ActiveNewsProvider>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent picks log */}
        <Card className="bg-card border-border">
          <div className="flex items-center justify-between px-4 py-2 bg-muted/20 border-b border-border">
            <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Pick Log</span>
            <span className="text-[10px] text-muted-foreground">{madePicks.length} / {totalPicks}</span>
          </div>
          <CardContent className="p-0">
            <div ref={pickLogRef} className="max-h-[300px] overflow-y-auto">
              {madePicks.length === 0 ? (
                <div className="py-8 text-center text-xs text-muted-foreground">No picks yet</div>
              ) : (
                [...madePicks].reverse().map(pick => (
                  <PickRow
                    key={pick.id}
                    pick={pick}
                    teamName={teamMap[pick.team_id] || '?'}
                    isMyPick={pick.team_id === currentTeam?.id}
                    showTeam
                    masked={isPickMasked(pick.pick_number)}
                    onPlayerClick={setDetailPlayerId}
                  />
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* SAB-459: the persisted queue. Reordering uses explicit move controls rather
            than drag-and-drop: the repo carries no DnD library, and buttons are
            keyboard-accessible and directly testable. The hook's `reorder(from, to)` is
            index-based, so a DnD surface can be layered on later without touching
            persistence. */}
        {currentTeam && queue.length > 0 && (
          <Card className="bg-card border-border" data-testid="nfl-draft-queue">
            <CardContent className="p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold text-foreground uppercase tracking-wider">
                  Your Queue
                </p>
                <span className="text-[10px] text-muted-foreground">
                  Auto-picks take these in order
                </span>
              </div>
              <ol className="space-y-1">
                {queue.map((pid, i) => {
                  const p = playerById.get(pid);
                  return (
                    <li
                      key={pid}
                      data-testid="queue-entry"
                      data-player-id={pid}
                      className="flex items-center gap-2 rounded-md bg-muted/20 px-2 py-1.5"
                    >
                      <span className="font-mono text-[10px] text-muted-foreground w-4 shrink-0">
                        {i + 1}
                      </span>
                      <span className="text-xs font-medium truncate flex-1">
                        {p ? p.full_name : 'Unavailable player'}
                      </span>
                      {p && (
                        <span className="text-[10px] text-muted-foreground shrink-0">
                          {p.position}
                        </span>
                      )}
                      <div className="flex items-center gap-0.5 shrink-0">
                        <Button
                          size="sm" variant="ghost"
                          className="h-6 w-6 p-0"
                          aria-label={`Move ${p ? p.full_name : 'player'} up`}
                          disabled={i === 0}
                          onClick={() => reorderQueue(i, i - 1)}
                        >
                          <ChevronUp className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm" variant="ghost"
                          className="h-6 w-6 p-0"
                          aria-label={`Move ${p ? p.full_name : 'player'} down`}
                          disabled={i === queue.length - 1}
                          onClick={() => reorderQueue(i, i + 1)}
                        >
                          <ChevronDown className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm" variant="ghost"
                          className="h-6 w-6 p-0 text-muted-foreground"
                          aria-label={`Remove ${p ? p.full_name : 'player'} from queue`}
                          onClick={() => removeFromQueue(pid)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </CardContent>
          </Card>
        )}

        {/* SAB-848: draft chat. Reuses MLB's DraftChatLive via the NFL_DRAFT_CHAT source
            -- nfl_chat_messages keyed on league_id, discriminated by message_type='draft'
            so commissioner broadcasts sharing that table stay out. Author names resolve
            from nfl_teams rather than a denormalised column. */}
        {currentLeague && (
          <div className="h-[320px]" data-testid="nfl-draft-chat">
            <DraftChatLive
              draftId={currentLeague.id}
              teamName={currentTeam?.name ?? null}
              source={NFL_DRAFT_CHAT}
              resolveAuthorName={resolveChatAuthor}
            />
          </div>
        )}

        {/* SAB-842: the draft board and team rosters, now available DURING the draft
            rather than only after it. Below the pick controls so the on-the-clock flow
            stays the top of the page. */}
        {draftViewsSection}

        <NFLPlayerDetailModal
          playerId={detailPlayerId}
          initialTab={detailTab}
          open={!!detailPlayerId}
          onOpenChange={(open) => { if (!open) { setDetailPlayerId(null); setDetailTab(undefined); } }}
        />

        {/* SAB-193: Sabotage panel + per-effect overlays.
            Mounted unconditionally during the live draft so the user can
            open it any time, and so realtime events still update its state
            even when closed. */}
        <NFLDraftSabotagePanel
          open={sabotagePanelOpen}
          onOpenChange={setSabotagePanelOpen}
          leagueId={currentLeague.id}
          myTeamId={currentTeam?.id ?? null}
          myUserId={user?.id ?? null}
          teams={teams.map((t) => ({ id: t.id, name: t.name }))}
          currentRound={currentRound}
          onPlayed={() => { refetchActive(); }}
        />

        {/* Blocked Punt — full-screen overlay while the server resolves the
            forced auto-pick. Auto-clears once the make-pick handler flips
            the event inactive (which fires the Realtime UPDATE → hook). */}
        {blockedPuntOnMe && (
          <div className="fixed inset-0 z-[80] bg-black/80 flex items-center justify-center pointer-events-auto">
            <div className="text-center">
              <span className="text-6xl block animate-bounce">🦶</span>
              <p className="text-white text-2xl font-bold mt-4">BLOCKED PUNT!</p>
              <p className="text-orange-400 text-base mt-1">Auto-picking for you…</p>
            </div>
          </div>
        )}
      </div>
    );
  }



  // ── Draft Completed: Results View ──
  // Compute aggregate stats for the summary card. byRound.length only counts
  // rounds with at least one made pick; total rounds is derived from the full
  // pick set (which includes empty placeholders during a live draft).
  const totalRounds = picks.length > 0 ? Math.max(...picks.map(p => p.round)) : 0;
  const totalAutoPicks = madePicks.filter(p => p.auto_picked).length;

  return (
    <div className="space-y-4 pb-20 md:pb-0">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-display font-bold text-foreground flex items-center gap-2">
          <Gavel className="h-5 w-5 text-primary" />
          Draft Results
        </h1>
        <Badge variant="outline" className="text-[10px] border-border text-muted-foreground">
          {madePicks.length} picks
        </Badge>
      </div>

      {/* Summary banner */}
      <Card className="bg-gradient-to-r from-primary/5 to-card border-border">
        <CardContent className="p-4 flex items-center gap-4">
          <div className="h-10 w-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
            <Trophy className="h-5 w-5 text-primary" />
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground">Draft Complete</p>
            <p className="text-xs text-muted-foreground">
              {totalRounds} rounds · {teams.length} teams · {madePicks.length} picks
              {totalAutoPicks > 0 && ` (${totalAutoPicks} auto)`}
            </p>
          </div>
        </CardContent>
      </Card>

      {draftViewsSection}

      <NFLPlayerDetailModal
        playerId={detailPlayerId}
        initialTab={detailTab}
        open={!!detailPlayerId}
        onOpenChange={(open) => { if (!open) { setDetailPlayerId(null); setDetailTab(undefined); } }}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Pick Row                                                           */
/* ------------------------------------------------------------------ */
function PickRow({
  pick, teamName, isMyPick, showTeam, masked = false, onPlayerClick,
}: {
  pick: DraftPick;
  teamName: string;
  isMyPick: boolean;
  showTeam: boolean;
  // SAB-450 (5) / SAB-33: true when this pick was made during the viewer's
  // currently-active Guess Who window. Photo-only mask — headshot stays,
  // name + position badge hide, click-to-modal disabled.
  masked?: boolean;
  onPlayerClick?: (id: string) => void;
}) {
  const pos = pick.player_position || '';
  const posClass = POS_COLORS[pos] || 'bg-muted/50 text-muted-foreground border-border';

  return (
    <div className={cn(
      'flex items-center gap-3 px-4 py-2.5 border-b border-border/30 last:border-b-0',
      isMyPick && 'bg-primary/[0.03] border-l-2 border-l-primary'
    )}>
      <span className="text-[10px] text-muted-foreground font-bold tabular-nums w-8 shrink-0">
        {pick.round}.{pick.pick_in_round}
      </span>
      {/* Position badge hidden under photo-only mask, matches board behavior. */}
      {!masked && (
        <Badge variant="outline" className={cn('text-[9px] px-1.5 py-0 shrink-0 font-bold', posClass)}>
          {pos || '?'}
        </Badge>
      )}
      <div className="h-8 w-8 rounded-full bg-muted/50 overflow-hidden shrink-0 border border-border/40">
        {pick.headshot_url ? (
          <img
            src={pick.headshot_url}
            // alt masked too — don't leak identity via screen reader.
            alt={masked ? '???' : (pick.player_name || '')}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-[10px] text-muted-foreground font-bold">
            {masked ? '?' : (pos || '?')}
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p
          className={cn(
            'text-sm font-medium truncate',
            masked
              ? 'text-muted-foreground italic'
              : cn('text-foreground', pick.player_id && 'cursor-pointer hover:text-primary transition-colors'),
          )}
          onClick={() => !masked && pick.player_id && onPlayerClick?.(pick.player_id)}
        >
          {masked ? '???' : (pick.player_name || '-')}
        </p>
        {showTeam && (
          <p className="text-[10px] text-muted-foreground truncate">{teamName}</p>
        )}
      </div>
      {/* SAB-534 Chunk 4: attribution. auto_picked covers cron auto, sabotage
          force_auto, and commissioner auto-draft-all ("Auto-drafted");
          picked_by_commissioner is a manual commissioner force pick. A pick is
          one or the other, never both. */}
      {pick.auto_picked ? (
        <Badge variant="outline" className="text-[8px] px-1 py-0 border-border text-muted-foreground shrink-0" title="Auto-drafted">
          AUTO
        </Badge>
      ) : pick.picked_by_commissioner ? (
        <Badge variant="outline" className="text-[8px] px-1 py-0 border-amber-500/40 text-amber-400 shrink-0" title="Commissioner pick">
          COMM
        </Badge>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Draft Recap — grades, awards, stats                                */
/* ------------------------------------------------------------------ */
function DraftRecap({
  picks,
  teams: allTeams,
  teamMap,
  myTeamId,
}: {
  picks: DraftPick[];
  teams: { id: string; name: string }[];
  teamMap: Record<string, string>;
  myTeamId?: string;
}) {
  // Calculate draft grades based on ADP value (pick_number vs ADP rank)
  const teamGrades = useMemo(() => {
    const results: {
      teamId: string;
      teamName: string;
      totalValue: number;
      pickCount: number;
      grade: string;
    }[] = [];

    for (const team of allTeams) {
      const teamPicks = picks.filter(p => p.team_id === team.id);
      if (teamPicks.length === 0) continue;

      // Value = sum of (pick_number - adp_rank) for each pick. Positive = got value
      let totalValue = 0;
      for (const p of teamPicks) {
        // If no ADP, assume they were drafted at value
        totalValue += (p.pick_number - p.pick_number) * 0; // placeholder since we don't have adp in picks
      }
      // Simple grade based on positional balance and pick count
      const positions = teamPicks.map(p => p.player_position).filter(Boolean);
      const hasQB = positions.includes('QB');
      const rbCount = positions.filter(p => p === 'RB').length;
      const wrCount = positions.filter(p => p === 'WR').length;
      const balance = (hasQB ? 1 : 0) + Math.min(rbCount, 2) + Math.min(wrCount, 2);

      let grade = 'C';
      if (balance >= 5) grade = 'A';
      else if (balance >= 4) grade = 'B+';
      else if (balance >= 3) grade = 'B';
      else if (balance >= 2) grade = 'C+';

      results.push({ teamId: team.id, teamName: team.name, totalValue, pickCount: teamPicks.length, grade });
    }

    return results.sort((a, b) => {
      const gradeOrder = ['A', 'B+', 'B', 'C+', 'C', 'D'];
      return gradeOrder.indexOf(a.grade) - gradeOrder.indexOf(b.grade);
    });
  }, [picks, allTeams]);

  // Awards
  const awards = useMemo(() => {
    const sorted = [...picks].sort((a, b) => a.pick_number - b.pick_number);
    const mrIrrelevant = sorted[sorted.length - 1];

    // Best value: earliest pick relative to what we'd expect (first pick in each round beyond round 1 with a high-value pos)
    const bestValue = sorted.find(p => p.pick_number > 10 && ['QB', 'RB', 'WR'].includes(p.player_position || ''));
    // Biggest reach: QB/K/P taken in round 1
    const biggestReach = sorted.find(p => p.round === 1 && ['K', 'P', 'DST', 'COACH'].includes(p.player_position || ''));

    return { mrIrrelevant, bestValue, biggestReach };
  }, [picks]);

  const GRADE_COLORS: Record<string, string> = {
    'A': 'text-green-400 bg-green-400/10',
    'B+': 'text-emerald-400 bg-emerald-400/10',
    'B': 'text-blue-400 bg-blue-400/10',
    'C+': 'text-amber-400 bg-amber-400/10',
    'C': 'text-orange-400 bg-orange-400/10',
    'D': 'text-red-400 bg-red-400/10',
  };

  return (
    <div className="space-y-4">
      {/* Draft Stats Banner */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="grid grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-lg font-bold text-foreground">{picks.length}</p>
              <p className="text-[10px] text-muted-foreground">Total Picks</p>
            </div>
            <div>
              <p className="text-lg font-bold text-foreground">{allTeams.length}</p>
              <p className="text-[10px] text-muted-foreground">Teams</p>
            </div>
            <div>
              <p className="text-lg font-bold text-foreground">{picks.filter(p => p.auto_picked).length}</p>
              <p className="text-[10px] text-muted-foreground">Auto-Drafted</p>
            </div>
            <div>
              <p className="text-lg font-bold text-amber-400">{picks.filter(p => p.picked_by_commissioner).length}</p>
              <p className="text-[10px] text-muted-foreground">Commissioner</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Draft Grades */}
      <Card className="bg-card border-border overflow-hidden">
        <div className="px-4 py-2 bg-muted/20 border-b border-border">
          <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Draft Grades</span>
        </div>
        <CardContent className="p-0">
          {teamGrades.map(tg => (
            <div
              key={tg.teamId}
              className={cn(
                'flex items-center gap-3 px-4 py-3 border-b border-border/30 last:border-b-0',
                tg.teamId === myTeamId && 'bg-primary/[0.03]'
              )}
            >
              <span className={cn(
                'text-base font-bold w-8 h-8 rounded-lg flex items-center justify-center',
                GRADE_COLORS[tg.grade] || 'text-muted-foreground bg-muted/10'
              )}>
                {tg.grade}
              </span>
              <div className="flex-1 min-w-0">
                <p className={cn('text-sm font-medium truncate', tg.teamId === myTeamId && 'text-primary')}>
                  {tg.teamName}
                </p>
                <p className="text-[10px] text-muted-foreground">{tg.pickCount} picks</p>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Awards */}
      <Card className="bg-card border-border overflow-hidden">
        <div className="px-4 py-2 bg-muted/20 border-b border-border">
          <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Awards</span>
        </div>
        <CardContent className="p-4 space-y-3">
          {awards.bestValue && (
            <div className="flex items-center gap-3">
              <span className="text-lg">💎</span>
              <div>
                <p className="text-xs font-bold text-foreground">Best Value</p>
                <p className="text-[10px] text-muted-foreground">
                  {awards.bestValue.player_name} ({awards.bestValue.player_position}), Pick {awards.bestValue.pick_number} by {teamMap[awards.bestValue.team_id]}
                </p>
              </div>
            </div>
          )}
          {awards.biggestReach && (
            <div className="flex items-center gap-3">
              <span className="text-lg">🤯</span>
              <div>
                <p className="text-xs font-bold text-foreground">Biggest Reach</p>
                <p className="text-[10px] text-muted-foreground">
                  {awards.biggestReach.player_name} ({awards.biggestReach.player_position}), Pick {awards.biggestReach.pick_number} by {teamMap[awards.biggestReach.team_id]}
                </p>
              </div>
            </div>
          )}
          {awards.mrIrrelevant && (
            <div className="flex items-center gap-3">
              <span className="text-lg">🏁</span>
              <div>
                <p className="text-xs font-bold text-foreground">Mr. Irrelevant</p>
                <p className="text-[10px] text-muted-foreground">
                  {awards.mrIrrelevant.player_name} ({awards.mrIrrelevant.player_position}), Last pick by {teamMap[awards.mrIrrelevant.team_id]}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
